import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Play, Lock, ArrowRight, Mic2 } from "lucide-react";
import { useRef } from "react";
import Seo from "../components/Seo";
import { Reveal, MaskedLines } from "../components/Reveal";
import TrailerPlayer from "../components/TrailerPlayer";
import { LOGO, CAST, HOST, SKETCH_GROUPS, SKETCHES, STILLS } from "../data/content";

function Marquee() {
  const items = ["LIVE STAND-UP", "ORIGINAL SKETCHES", "HOSTED BY TODD BRIDGES", "LAUGH AT L.A."];
  const row = [...items, ...items, ...items];
  return (
    <div className="relative overflow-hidden border-y border-[#F59E0B]/30 bg-[#12121A] py-3" aria-hidden="true">
      <div className="marquee-track flex w-max whitespace-nowrap">
        {[0, 1].map((half) => (
          <div key={half} className="flex">
            {row.map((t, i) => (
              <span key={`${half}-${i}`} className="font-display text-lg sm:text-xl tracking-[0.2em] text-gray-400 px-6 flex items-center gap-6">
                {t} <span className="text-[#E11D48]">★</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Hero() {
  const ref = useRef(null);
  const playerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yPlayer = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="spotlight-bg relative overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: yBg }} className="pointer-events-none absolute inset-0">
        <div className="spotlight-cone absolute inset-x-0 top-0 h-[80vh]" />
        <img src="/assets/brand/key-art.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.12]" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0E]/60 to-[#0A0A0E]" />
      </motion.div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 pt-32 pb-24 sm:pt-40 sm:pb-32 grid lg:grid-cols-2 items-center gap-12">
        <div className="text-center lg:text-left">
          <motion.img
            src={LOGO} alt="Laugh at L.A. official logo"
            className="mx-auto lg:mx-0 w-28 sm:w-36 drop-shadow-[0_0_30px_rgba(225,29,72,0.35)]"
            initial={{ opacity: 0, scale: 0.85, rotate: -4 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-logo"
          />
          <div className="mt-6 flex flex-wrap items-center justify-center lg:justify-start gap-3">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F59E0B] neon-gold" data-testid="hero-eyebrow">
              A Providence Film Group Comedy Series
            </p>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-500 to-yellow-600 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-black shadow-[0_0_20px_rgba(245,158,11,0.4)]" data-testid="anniversary-badge">
              20th Anniversary
            </span>
          </div>
          <h1 className="mt-4 font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.95] text-white neon-red" data-testid="hero-headline">
            <MaskedLines lines={["L.A. TAKES", "THE MIC."]} />
          </h1>
          <motion.p
            className="mt-6 max-w-xl text-base sm:text-lg text-gray-300 leading-relaxed mx-auto lg:mx-0"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.7 }}
            data-testid="hero-logline"
          >
            Hosted by <span className="text-white font-semibold">Todd Bridges</span>, Laugh at L.A. brings the city's stand-up comics and original scripted sketches together in front of a live audience.
          </motion.p>
          <motion.div
            className="mt-8 flex flex-wrap justify-center lg:justify-start gap-4"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
          >
            <button onClick={() => playerRef.current?.play()} data-testid="hero-watch-trailer"
              className="group inline-flex items-center gap-2 rounded-full bg-[#E11D48] px-7 py-3.5 font-semibold text-white shadow-[0_0_25px_rgba(225,29,72,0.5)] transition-all hover:bg-[#F43F5E] hover:shadow-[0_0_35px_rgba(225,29,72,0.7)]">
              <Play size={18} className="transition-transform group-hover:scale-110" /> Watch the Trailer
            </button>
            <Link to="/watch#episode" data-testid="hero-unlock-episode"
              className="inline-flex items-center gap-2 rounded-full border border-[#F59E0B]/60 px-7 py-3.5 font-semibold text-[#F59E0B] transition-colors hover:bg-[#F59E0B]/10">
              <Lock size={16} /> Unlock an Episode
            </Link>
          </motion.div>
        </div>
        <motion.div
          style={{ y: yPlayer }}
          initial={{ opacity: 0, y: 30, rotate: 1.5 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <TrailerPlayer ref={playerRef} testId="hero-trailer-player" />
        </motion.div>
      </div>
      <Marquee />
    </section>
  );
}

function Intro() {
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 py-20 sm:py-28" data-testid="intro-section">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F59E0B]">The Series</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl text-white leading-tight">
            "Today's version of <span className="text-[#E11D48]">In Living Color</span> meets <span className="text-[#E11D48]">Chappelle's Show</span>"
          </h2>
          <p className="mt-6 text-gray-300 leading-relaxed">
            A foremost comedy experience that begins in Los Angeles. The show scours the local comedy circuit — the bars and clubs — to discover fresh new talent, cast in hysterical, timely and relevant sketches packed with mainstream humor. The comics also perform their stand-up live, while the sketches air between sets.
          </p>
          <Link to="/show" data-testid="intro-learn-more" className="mt-8 inline-flex items-center gap-2 text-[#F59E0B] font-semibold link-gold">
            About the show <ArrowRight size={16} />
          </Link>
        </Reveal>
        <Reveal delay={0.15} className="relative">
          <div className="absolute -inset-4 rounded-2xl bg-gradient-to-tr from-[#E11D48]/20 to-[#F59E0B]/20 blur-2xl" aria-hidden="true" />
          <img src="/assets/brand/standup-poster.jpg" alt="Stand-up comedy poster from Laugh at L.A."
            className="relative rounded-2xl border border-white/10 object-cover w-full max-h-[520px]" loading="lazy" data-testid="intro-poster" />
        </Reveal>
      </div>
    </section>
  );
}

function FeaturedCast() {
  const featured = [HOST, ...CAST.slice(0, 5)];
  return (
    <section className="bg-[#12121A] border-y border-white/5" data-testid="featured-cast-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20 sm:py-28">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F59E0B]">On the Bill</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-white">Featured Cast</h2>
          </div>
          <Link to="/cast" data-testid="featured-cast-all" className="inline-flex items-center gap-2 text-[#F59E0B] font-semibold link-gold">
            Full cast <ArrowRight size={16} />
          </Link>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6">
          {featured.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.07}>
              <Link to="/cast" data-testid={`featured-cast-${c.name.toLowerCase().replace(/[\s.]+/g, "-")}`} className="group block">
                <div className="relative overflow-hidden rounded-xl border border-white/10 aspect-[3/4] bg-[#181824]">
                  {c.img ? (
                    <img src={c.img} alt={`${c.name} headshot`} loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="flex h-full items-center justify-center"><Mic2 size={40} className="text-gray-600" /></div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-3 pt-10">
                    <p className="text-sm font-semibold text-white">{c.name}</p>
                    <p className="text-xs text-[#F59E0B]">{c.role}</p>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function SketchPreview() {
  const preview = [SKETCH_GROUPS[0].sketches[0], SKETCHES[2], SKETCHES[3], SKETCH_GROUPS[1].sketches[1]];
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 py-20 sm:py-28" data-testid="sketch-preview-section">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F59E0B]">The Sketch Engine</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl text-white">Tonight's Lineup</h2>
        </div>
        <Link to="/sketches" data-testid="sketch-preview-all" className="inline-flex items-center gap-2 text-[#F59E0B] font-semibold link-gold">
          All sketches <ArrowRight size={16} />
        </Link>
      </Reveal>
      <div className="mt-12 grid sm:grid-cols-2 gap-5">
        {preview.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.08}>
            <div className="group h-full rounded-xl border border-white/10 bg-[#181824]/70 backdrop-blur-xl p-6 transition-colors hover:border-[#F59E0B]/40" data-testid={`sketch-card-${s.title.toLowerCase().replace(/[\s.—]+/g, "-")}`}>
              <p className="font-display text-2xl text-white group-hover:text-[#F59E0B] transition-colors">{s.title}</p>
              <p className="mt-3 text-sm text-gray-400 leading-relaxed">{s.description}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function StillsStrip() {
  return (
    <section className="bg-[#12121A] border-y border-white/5 overflow-hidden" data-testid="stills-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F59E0B]">From the Stage & Screen</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-white">Shot in front of a live crowd</h2>
          </div>
          <Link to="/bts" data-testid="stills-bts-link" className="inline-flex items-center gap-2 text-[#F59E0B] font-semibold link-gold">
            Behind the scenes <ArrowRight size={16} />
          </Link>
        </Reveal>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-4">
          {STILLS.map((s, i) => (
            <Reveal key={s.src} delay={(i % 3) * 0.08}>
              <img src={s.src} alt={s.alt} loading="lazy"
                className="w-full aspect-video object-cover rounded-xl border border-white/10 transition-transform duration-500 hover:scale-[1.02]" />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function JoinBanner() {
  return (
    <section className="relative overflow-hidden border-t border-white/5" data-testid="join-banner-section">
      <div className="absolute inset-0 bg-gradient-to-r from-[#E11D48]/15 via-transparent to-[#F59E0B]/15" aria-hidden="true" />
      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 py-20 text-center">
        <Reveal>
          <h2 className="font-display text-4xl sm:text-6xl text-white">Get on the List</h2>
          <p className="mt-4 text-gray-300 max-w-xl mx-auto">
            First word on sketches, stand-up clips, live tapings, and where the Laugh @ tour stops next.
          </p>
          <Link to="/join" data-testid="join-banner-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#F59E0B] px-8 py-4 font-bold text-black transition-transform hover:scale-105">
            Join the List <ArrowRight size={18} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Seo />
      <Hero />
      <Intro />
      <FeaturedCast />
      <SketchPreview />
      <StillsStrip />
      <JoinBanner />
    </main>
  );
}
